import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { SlCloudUpload } from "react-icons/sl";

const CustomField = React.memo(({
  type,
  name,
  placeholder,
  className,
  handleChange,
  handleBlur,
  value,
  error,
  touched,
  options,
  setFieldValue,
  timeIntervals,
  label,
  helperText,
  maxLength,
  defaultHandleChange = true,
  uploadFiletext,
  extraClass,
  selectPropClass,
  disabled,
  onKeyDown,
  forceLightMode = false,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [isFocused, setIsFocused] = useState(false);

  // Dark mode check - simplified to avoid per-instance observers
  const isDarkMode = () => !forceLightMode && document.documentElement.classList.contains("dark");
  const [darkMode, setDarkMode] = useState(isDarkMode());

  // Check once on mount or when forceLightMode changes
  useEffect(() => {
    setDarkMode(isDarkMode());
  }, [forceLightMode]);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleChangeForFloatingInput = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    handleChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = (event) => {
    setIsFocused(false);
    handleBlur(event);
  };

  const handleSelectChange = (event) => {
    const selectedValue = event.target.value;
    setInputValue(selectedValue);
    handleChange({
      target: {
        name,
        value: selectedValue,
      },
    });
  };

  return (
    <div className='relative'>
      {type === "select" ? (
        <TextField
  name={name}
  select
  label={label}
  onChange={handleSelectChange}
  value={value}
  disabled={disabled}
  size="small"
  helperText={helperText}
  className={`w-full ${extraClass}`}
  sx={{
    "& .MuiOutlinedInput-root": {
      height: 32,

      "& fieldset": {
        borderColor: "#e7e5e5", // Light gray border
      },

      "&:hover fieldset": {
        borderColor: "#D1D5DB",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#D1D5DB",
        borderWidth: "1px",
      },

      ...(darkMode && {
        backgroundColor: "#1f2937",

        "& fieldset": {
          borderColor: "#4B5563",
        },

        "&:hover fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-focused fieldset": {
          borderColor: "#6B7280",
        },
      }),
    },

    "& .MuiInputLabel-root": {
      color: "#313333",
      fontSize: "0.80rem",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#9CA3AF",
    },

    ...(darkMode && {
      "& .MuiInputLabel-root": {
        color: "#FFFFFF",
      },

      "& .MuiInputBase-input": {
        color: "#FFFFFF",
      },

      "& .MuiSvgIcon-root": {
        color: "#FFFFFF",
      },
    }),
  }}
  SelectProps={{
    native: false,
    className: `${selectPropClass} ${
      darkMode ? "dark:bg-[#1f2937] dark:text-white" : ""
    }`,
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: darkMode ? "#374151" : "#fff",
          "& .MuiMenuItem-root": {
            color: darkMode ? "#fff" : "#000",

            "&.Mui-selected": {
              backgroundColor: darkMode ? "#4B5563" : "#2563EB",
              color: "#fff",
            },

            "&:hover": {
              backgroundColor: darkMode ? "#4B5563" : "#BFDBFE",
            },
          },
        },
      },
    },
    IconComponent: (props) => (
      <svg
        {...props}
        className={`w-5 h-5 ${
          darkMode ? "text-white" : "text-gray-500"
        } ${disabled ? "opacity-50" : ""}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ),
  }}
  InputProps={{
    className: `
      w-full h-8 p-1
      bg-white text-black
      ${darkMode ? "dark:bg-[#1f2937] dark:text-white" : ""}
      ${disabled ? "!text-gray-400 dark:!text-gray-400" : ""}
    `,
  }}
>
  {options.map((option) => (
    <MenuItem
      key={option.value}
      value={option.value}
      className={
        darkMode
          ? "text-xs dark:bg-[#170f2a] dark:text-white"
          : "text-xs text-gray-800 hover:bg-blue-200"
      }
    >
      {option.label}
    </MenuItem>
  ))}
</TextField>
      ) : type === "file" ? (
        <div className="relative w-full">
          <input
            type='file'
            name={name}
            className={`absolute inset-0 w-full bg-white h-full opacity-0 cursor-pointer dark:border-white ${
              className || ""
            }`}
            onChange={handleChange}
            onBlur={handleBlur}
            {...rest}
          />
          <div
  className={`
    w-full
    h-[90px]
    border
    border-dashed
    border-gray-300
    rounded-lg
    flex
    flex-col
    items-center
    justify-center
    text-center
    ${
      darkMode
        ? "dark:text-white dark:border-gray-600 dark:bg-[#1f2937]"
        : "bg-transparent"
    }
    ${extraClass}
  `}
>
            <span className="text-blue-500 text-xl mb-1">
              <SlCloudUpload />
            </span>
             <span className="font-medium text-blue-600">{value && value.name ? value.name : uploadFiletext}</span>
             <span className="text-xs text-gray-500 mt-1">
    PNG, JPG or WEBP (Max. 5MB)
  </span>
          </div>
          
          {value && value.name && (
            <p className='text-sm text-gray-600 dark:text-white'>
              {value.name}
            </p>
          )}
        </div>
      ) : type === "time" ? (
        <TextField
          id={name}
          label={label}
          type="time"
          onChange={handleChange}
          value={value}
          size="small"
          className="w-full"
          {...rest}
          sx={{
  "& .MuiOutlinedInput-root": {
    height: 32,

    "& fieldset": {
      borderColor: "#E5E7EB",
    },

    "&:hover fieldset": {
      borderColor: "#D1D5DB",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#D1D5DB",
      borderWidth: "1px",
    },

    ...(darkMode && {
      backgroundColor: "#1F2937",

      "& fieldset": {
        borderColor: "#374151",
      },

      "&:hover fieldset": {
        borderColor: "#4B5563",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#3B82F6",
        borderWidth: "1.5px",
      },
    }),
  },

  // Label
  "& .MuiInputLabel-root": {
    color: "#374151",
    fontSize: "0.80rem",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#374151",
  },

  // Input text
  "& .MuiInputBase-input": {
    fontSize: "0.80rem",
    color: "#000000",
  },

  ...(darkMode && {
    "& .MuiInputLabel-root": {
      color: "#D1D5DB",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#60A5FA",
    },

    "& .MuiInputBase-input": {
      color: "#FFFFFF",
      WebkitTextFillColor: "#FFFFFF",
    },

    "& .MuiSvgIcon-root": {
      color: "#FFFFFF",
    },

    "& input::-webkit-calendar-picker-indicator": {
      filter: "invert(1)",
      opacity: 0.8,
      cursor: "pointer",
    },
  }),
}}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
  className: `
    w-full h-8 bg-white
    ${
      darkMode
        ? "dark:bg-[#1F2937] dark:text-white dark:[color-scheme:dark]"
        : ""
    }
  `,
  sx: {
    fontSize: "0.80rem",

    ...(darkMode && {
      color: "#FFFFFF",
      WebkitTextFillColor: "#FFFFFF",
    }),
  },
}}
        />
      ) : type === "date" ? (
       <TextField
  id={name}
  label={label}
  type="date"
  onChange={handleChange}
  value={value}
  size="small"
  className="w-full"
  error={error}
  helperText={helperText}
  {...rest}
  sx={{
    "& .MuiOutlinedInput-root": {
      height: 32,

      "& fieldset": {
        borderColor: "#E5E7EB",
      },

      "&:hover fieldset": {
        borderColor: "#D1D5DB",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#D1D5DB",
        borderWidth: "1px",
      },

      ...(darkMode && {
        backgroundColor: "#1f2937",

        "& fieldset": {
          borderColor: "#4B5563",
        },

        "&:hover fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-focused fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-disabled": {
          backgroundColor: "#4B5563",
        },
      }),
    },

    // Label
    "& .MuiInputLabel-root": {
      color: "#313333",
      fontSize: "0.80rem",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#313333",
    },

    // Input text
    "& .MuiInputBase-input": {
      fontSize: "0.80rem",
      color: "#000",
    },

    ...(darkMode && {
      "& .MuiInputLabel-root": {
        color: "#FFFFFF",
      },

      "& .MuiInputLabel-root.Mui-focused": {
        color: "#FFFFFF",
      },

      "& .MuiInputBase-input": {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
        fontSize: "0.80rem",
      },

      "& .MuiInputBase-input.Mui-disabled": {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
      },

      "& .MuiSvgIcon-root": {
        color: "#FFFFFF",
      },
    }),
  }}
  InputLabelProps={{
    shrink: true,
    style: {
      fontSize: "0.80rem",
    },
  }}
  InputProps={{
    className: `
      w-full h-8 bg-white
      ${
        darkMode
          ? "dark:bg-[#1f2937] dark:text-white dark:[color-scheme:dark] dark:[&::-webkit-calendar-picker-indicator]:filter-invert"
          : ""
      }
    `,
    sx: {
      fontSize: "0.80rem",

      ...(darkMode && {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
      }),
    },
  }}
  FormHelperTextProps={{
    sx: {
      fontSize: "0.75rem",
      color: darkMode ? "#D1D5DB" : "#6B7280",
    },
  }}
/>
      ) : type === "textarea" ? (
        <TextField
  id={name}
  label={label}
  name={name}
  value={value}
  onChange={handleChange}
  onBlur={handleBlur}
  placeholder={placeholder}
  multiline
  minRows={rest.rows || 1}
  maxRows={rest.maxRows || 6}
  size="small"
  className="w-full"
  disabled={disabled}
  error={!!error}
  helperText={helperText}
  {...rest}
  sx={{
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",

      "& fieldset": {
        borderColor: "#E5E7EB",
      },

      "&:hover fieldset": {
        borderColor: "#D1D5DB",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#D1D5DB",
        borderWidth: "1px",
      },

      ...(darkMode && {
        backgroundColor: "#1f2937",

        "& fieldset": {
          borderColor: "#4B5563",
        },

        "&:hover fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-focused fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-disabled": {
          backgroundColor: "#4B5563",
        },
      }),
    },

    // Label
    "& .MuiInputLabel-root": {
      color: "#313333",
      fontSize: "0.80rem",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#313333",
    },

    // Input Text
    "& .MuiInputBase-input": {
      fontSize: "0.80rem",
      color: "#000000",
    },

    ...(darkMode && {
      "& .MuiInputLabel-root": {
        color: "#FFFFFF",
      },

      "& .MuiInputLabel-root.Mui-focused": {
        color: "#FFFFFF",
      },

      "& .MuiInputBase-input": {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
        fontSize: "0.80rem",
      },

      "& .MuiInputBase-input.Mui-disabled": {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
      },
    }),
  }}
  InputProps={{
    className: `
      w-full
      bg-white text-black
      ${darkMode ? "dark:bg-[#1f2937] dark:text-white" : ""}
      ${disabled ? "!text-gray-400 dark:!text-gray-400" : ""}
    `,
    sx: {
      fontSize: "0.80rem",

      ...(darkMode &&
        disabled && {
          backgroundColor: "#4B5563",
          color: "#FFFFFF",
          WebkitTextFillColor: "#FFFFFF",
        }),
    },
  }}
  InputLabelProps={{
    style: {
      fontSize: "0.80rem",
    },
  }}
  FormHelperTextProps={{
    sx: {
      fontSize: "0.75rem",
      color: darkMode ? "#D1D5DB" : "#6B7280",
    },
  }}
/>
      ) : type === "text" || type === "textWithError" || type === "password" || type === "tel" || type === "email" ? (
        <TextField
  helperText={helperText}
  id={name}
  label={label}
  name={name}
  type={type}
  value={value}
  onChange={handleChange}
  onBlur={handleBlur}
  onKeyDown={onKeyDown}
  size="small"
  className="w-full"
  disabled={disabled}
  error={type === "textWithError" ? error : undefined}
  {...rest}
  sx={{
    "& .MuiOutlinedInput-root": {
      height: 32,

      "& fieldset": {
        borderColor: "#E5E7EB",
      },

      "&:hover fieldset": {
        borderColor: "#D1D5DB",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#D1D5DB",
        borderWidth: "1px",
      },

      ...(darkMode && {
        backgroundColor: "#1f2937",

        "& fieldset": {
          borderColor: "#4B5563",
        },

        "&:hover fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-focused fieldset": {
          borderColor: "#6B7280",
        },

        "&.Mui-disabled": {
          backgroundColor: "#4B5563",
        },
      }),
    },

    // Label
    "& .MuiInputLabel-root": {
      color: "#313333",
      fontSize: "0.80rem",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#313333",
    },

    // Input text
    "& .MuiInputBase-input": {
      fontSize: "0.80rem",
      color: "#000",
    },

    ...(darkMode && {
      "& .MuiInputLabel-root": {
        color: "#FFFFFF",
      },

      "& .MuiInputLabel-root.Mui-focused": {
        color: "#FFFFFF",
      },

      "& .MuiInputBase-input": {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
        fontSize: "0.80rem",
      },

      "& .MuiInputBase-input.Mui-disabled": {
        color: "#FFFFFF",
        WebkitTextFillColor: "#FFFFFF",
      },

      "& .MuiSvgIcon-root": {
        color: "#FFFFFF",
      },
    }),
  }}
  InputProps={{
    ...rest.InputProps,
    className: `
      w-full h-8
      bg-white text-black
      ${darkMode ? "dark:bg-[#1f2937] dark:text-white" : ""}
      ${disabled ? "!text-gray-400 dark:!text-gray-400" : ""}
    `,
    sx: {
      fontSize: "0.80rem",

      ...(darkMode &&
        disabled && {
          backgroundColor: "#4B5563",
          color: "#FFFFFF",
          WebkitTextFillColor: "#FFFFFF",
        }),
    },
  }}
  InputLabelProps={{
    style: {
      fontSize: "0.80rem",
    },
  }}
  FormHelperTextProps={{
    sx: {
      fontSize: "0.75rem",
      color: darkMode ? "#D1D5DB" : "#6B7280",
    },
  }}
/>
      ) : (
        <div className="relative">
  <input
    type={type}
    id={name}
    className={`
      block w-full h-8 px-2.5 pt-0.5 pb-0.5
      text-xs
      text-gray-900
      bg-white
      border border-[#E5E7EB]
      rounded-lg
      appearance-none
      focus:outline-none
      focus:ring-0
      focus:border-[#D1D5DB]
      transition-colors
      ${
        darkMode
          ? "dark:bg-[#020617] dark:text-white dark:border-[#4B5563] dark:focus:border-[#6B7280]"
          : ""
      }
      ${className || ""}
    `}
    placeholder=""
    value={inputValue}
    onChange={handleChangeForFloatingInput}
    onFocus={handleInputFocus}
    onBlur={handleInputBlur}
    {...rest}
  />

  <label
    htmlFor={name}
    className={`
      absolute left-2
      px-1
      text-xs
      transition-all duration-200
      origin-left
      pointer-events-none
      ${
        inputValue
          ? "-translate-y-4 scale-75 top-2"
          : "top-1/2 -translate-y-1/2"
      }
      bg-white
      text-[#374151]
      ${
        darkMode
          ? "dark:bg-[#020617] dark:text-[#D1D5DB]"
          : ""
      }
    `}
  >
    {placeholder}
  </label>
</div>
      )}
      {error && touched && (
        <div className='text-red-600 text-xs dark:text-red-400'>{error}</div>
      )}
    </div>
  );
});

export default CustomField;
